import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CtaType, HOST_PLATFORMS, OPEN_MODES, OpenMode, TRACKING_ELEMENTS, TrackingElement } from 'app/shared/consts';
import { ActivityFilter, resolveActivityFilter } from 'app/shared/consts/activity';
import { ActivityItem, ActivitySegment, ActivityType } from 'app/data/types/activity';
import { WebClickClickTypeUnion } from 'app/data/types/tracking';
import { useSdkHostInfo } from 'app/shared/contexts/host-info';
import { useMatchedRoute } from 'app/shared/hooks/use-matched-route';
import { useOpenParams } from 'app/shared/hooks/use-open-params';
import { useTrackWebClick } from 'app/shared/hooks/use-track-web-click';

/** Identifies the record a click is about, for surfaces that hold a booking rather than a feed item. */
export interface ActivityIdentity {
  activityType: ActivityType;
  itemUuid: string;
}

interface TileParams {
  item: ActivityItem;
  openMode: OpenMode;
  destinationRoute?: string | null;
}

interface TileCtaParams extends TileParams {
  /** Open-ended: a tile's actions come from the feed payload, so the vocabulary isn't ours to close. */
  ctaType: string;
}

interface DetailsCtaParams extends ActivityIdentity {
  ctaType: CtaType;
  /** Defaults to a deeplink out; pass `modal` for a CTA that opens something in place. */
  openMode?: OpenMode;
  destinationRoute?: string | null;
}

interface RatingParams {
  item: ActivityItem;
  rating: number;
  candidateUuid?: string;
}

interface StarClickParams extends RatingParams {
  /** False means this tap IS the submission; true means it opens the comment modal instead. */
  commentsEnabled: boolean;
}

export interface ActivityTracking {
  trackSegmentTab: (toSegment: ActivitySegment) => void;
  trackFilterChip: (toFilter: ActivityFilter) => void;
  trackTile: (params: TileParams) => void;
  trackTileCta: (params: TileCtaParams) => void;
  trackDetailsCta: (params: DetailsCtaParams) => void;
  trackEmptyCta: (destinationRoute?: string | null) => void;
  trackCreateCta: (destinationRoute?: string | null) => void;
  trackCancelSubmit: (identity: ActivityIdentity) => void;
  trackRatingStarClick: (params: StarClickParams) => void;
  trackRatingCommentSubmit: (params: RatingParams) => void;
  trackPullRefresh: () => void;
}

export const toActivityIdentity = (item: ActivityItem): ActivityIdentity => ({
  activityType: item.type,
  itemUuid: item.id,
});

const identityProps = ({ activityType, itemUuid }: ActivityIdentity): Record<string, string> => ({
  activity_type: activityType,
  item_uuid: itemUuid,
});

const identityOf = (item: ActivityItem): Record<string, string> => identityProps(toActivityIdentity(item));

/**
 * A deeplink's origin + path, dropping only the query. `destination_route` is a group-by key
 * downstream (dbt matches it with LIKE), and the query is what would fragment it — every deeplink
 * carries the launch params and per-record ids.
 *
 * The origin has to stay: a module LANDING deeplink is the bare base url, so stripping it collapses
 * every module to "/" and the field stops identifying anything.
 */
export const toDestinationRoute = (url: string | null): string | null => {
  if (!url) {
    return null;
  }

  try {
    const { origin, pathname } = new URL(url);

    return `${origin}${pathname}`;
  } catch {
    return null;
  }
};

/** A modal opens in place, so it's an `action`; everything else leaves the feed. */
const clickTypeFor = (openMode: OpenMode): WebClickClickTypeUnion =>
  (openMode === OPEN_MODES.MODAL ? 'action' : 'navigation');

/**
 * Assembles every click in the VIS-1539 catalog. Call sites pass only what is specific to the event —
 * the base dimensions (route, building, host platform, segment, filter) are resolved here so they can
 * never be forgotten at a call site.
 *
 * `host_platform` is load-bearing rather than decorative: dbt derives `app_platform` from it and the
 * tenant models filter on that, so a click without one is dropped from reporting entirely. Hence the
 * fallback — an unknown host is reported as mobile, matching miniapp-content.
 */
export const useActivityTracking = (): ActivityTracking => {
  const trackClick = useTrackWebClick();
  const route = useMatchedRoute();
  const { buildingUuid } = useOpenParams();
  const { hostPlatform } = useSdkHostInfo();
  const [searchParams] = useSearchParams();

  const segment = (searchParams.get('tab') as ActivitySegment | null) ?? 'upcoming';
  const filter = resolveActivityFilter(searchParams.get('filter'));

  const baseProperties = useMemo(
    (): Record<string, string> => ({
      building_uuid: buildingUuid,
      host_platform: hostPlatform ?? HOST_PLATFORMS.MOBILE,
      segment,
      filter,
    }),
    [buildingUuid, hostPlatform, segment, filter],
  );

  const track = useCallback(
    (
      elementName: TrackingElement,
      clickType: WebClickClickTypeUnion,
      additionalProperties: Record<string, string> = {},
      destinationRoute: string | null = null,
    ): void => {
      trackClick({
        route,
        element_name: elementName,
        click_type: clickType,
        destination_route: destinationRoute,
        additional_properties: { ...baseProperties, ...additionalProperties },
      });
    },
    [trackClick, route, baseProperties],
  );

  return useMemo(
    (): ActivityTracking => ({
      // The segmented control and the filter chips DO navigate — they push a new query string — they just
      // land on the route they started from. Reporting the destination anyway keeps the field meaningful
      // for every click that moves the user, instead of null-where-it-happens-to-be-the-same-page.
      trackSegmentTab: (toSegment) => track(TRACKING_ELEMENTS.SEGMENT_TAB, 'action', { to_segment: toSegment }, route),
      trackFilterChip: (toFilter) => track(TRACKING_ELEMENTS.FILTER_CHIP, 'action', { to_filter: toFilter }, route),
      trackTile: ({ item, openMode, destinationRoute }) =>
        track(
          TRACKING_ELEMENTS.TILE,
          clickTypeFor(openMode),
          { ...identityOf(item), open_mode: openMode },
          destinationRoute ?? null,
        ),
      trackTileCta: ({ item, ctaType, openMode, destinationRoute }) =>
        track(
          TRACKING_ELEMENTS.TILE_CTA,
          clickTypeFor(openMode),
          { ...identityOf(item), cta_type: ctaType, open_mode: openMode },
          destinationRoute ?? null,
        ),
      trackDetailsCta: ({ activityType, itemUuid, ctaType, openMode = OPEN_MODES.DEEPLINK, destinationRoute }) =>
        track(
          TRACKING_ELEMENTS.DETAILS_CTA,
          clickTypeFor(openMode),
          { ...identityProps({ activityType, itemUuid }), cta_type: ctaType, open_mode: openMode },
          destinationRoute ?? null,
        ),
      trackEmptyCta: (destinationRoute) => track(TRACKING_ELEMENTS.EMPTY_CTA, 'navigation', {}, destinationRoute ?? null),
      trackCreateCta: (destinationRoute) =>
        track(TRACKING_ELEMENTS.CREATE_CTA, 'navigation', {}, destinationRoute ?? null),
      trackCancelSubmit: (identity) => track(TRACKING_ELEMENTS.CANCEL_SUBMIT, 'action', identityProps(identity)),
      trackRatingStarClick: ({ item, rating, commentsEnabled, candidateUuid }) =>
        track(TRACKING_ELEMENTS.RATING_STAR_CLICK, 'action', {
          ...identityOf(item),
          rating: String(rating),
          comments_enabled: String(commentsEnabled),
          ...(candidateUuid ? { candidate_uuid: candidateUuid } : {}),
        }),
      trackRatingCommentSubmit: ({ item, rating, candidateUuid }) =>
        track(TRACKING_ELEMENTS.RATING_COMMENT_SUBMIT, 'action', {
          ...identityOf(item),
          rating: String(rating),
          ...(candidateUuid ? { candidate_uuid: candidateUuid } : {}),
        }),
      trackPullRefresh: () => track(TRACKING_ELEMENTS.PULL_REFRESH, 'action'),
    }),
    [track, route],
  );
};
